import Validator, {
  date,
  equal,
  exclude,
  include,
  length,
  pattern,
  present,
  range,
  year,
} from '@briza/wegood'
import Engine from '@briza/illogical'

import {
  AnswerValue,
  Question,
  QuestionType,
  QuestionValidationRule,
  QuestionValidationRuleType,
  UnansweredValue,
} from './air-question.types'
import { Status, Validation } from '../../types/status.types'
import { Address } from '../air-address/air-address.types'
import { AnswerMap } from '../air-questionnaire/air-questionnaire.types'
import { ValidationRule } from '@briza/wegood/types/rule'
import { isUndefined } from '../../utils/helpers'

const engine = new Engine()

const validationRulesMap: Record<
  QuestionValidationRuleType,
  (
    rule: QuestionValidationRule,
    defaultErrorMessage?: string,
    answers?: AnswerMap,
  ) => ValidationRule
> = {
  [QuestionValidationRuleType.Present]: (_, message) =>
    present(message ?? 'Please provide an answer'),
  [QuestionValidationRuleType.Range]: (rule, message) => {
    const min = rule.min?.toLocaleString()
    const max = rule.max?.toLocaleString()
    return range(
      message ??
        (isUndefined(rule.min) && !isUndefined(rule.max)
          ? `Please provide an answer that is at most ${max}`
          : !isUndefined(rule.min) && isUndefined(rule.max)
            ? `Please provide an answer that is at least ${min}`
            : `Please provide an answer between ${min} and ${max}`),
      rule.min,
      rule.max,
    )
  },
  [QuestionValidationRuleType.Equal]: (rule, message) =>
    equal(message ?? `Your answer must exactly be ${rule.value}`, rule.value),
  [QuestionValidationRuleType.Pattern]: (rule, message) =>
    pattern(
      message ?? `Please provide an answer that matches this pattern "${rule.pattern}"`,
      rule.pattern as unknown as RegExp,
    ),
  [QuestionValidationRuleType.Length]: (rule, message) => {
    const min = rule.min?.toLocaleString()
    const max = rule.max?.toLocaleString()
    return length(
      message ??
        (isUndefined(rule.min) && !isUndefined(rule.max)
          ? `Please provide an answer no longer than ${max} characters`
          : !isUndefined(rule.min) && isUndefined(rule.max)
            ? `Please provide an answer with at least ${min} character${rule.min > 1 ? 's' : ''}`
            : `Please provide an answer between ${min} and ${max} characters`),
      rule.min,
      rule.max,
    )
  },
  [QuestionValidationRuleType.Exclude]: (rule, message) =>
    exclude(
      message ?? `Your answer cannot be one of "${rule.exclusions?.join(', ')}"`,
      rule.exclusions || [],
    ),
  [QuestionValidationRuleType.Include]: (rule, message) =>
    include(
      message ?? `Your answer must be one of "${rule.inclusions?.join(', ')}"`,
      rule.inclusions || [],
    ),
  [QuestionValidationRuleType.Date]: (rule, message) =>
    date(message ?? 'Please select a valid date', rule.start, rule.end),
  [QuestionValidationRuleType.Year]: (rule, message) =>
    year(message ?? 'Please enter a valid year', rule.start, rule.end),
  [QuestionValidationRuleType.Condition]:
    ({ condition }, message, answers) =>
    (): true | string =>
      !condition || !engine.evaluate(condition, answers ?? {})
        ? true
        : (message ?? 'Your answer is invalid due to validation condition'),
}

export function validateQuestion(
  question: Question,
  answers: AnswerMap,
  answer: AnswerValue | undefined,
): Validation {
  const errorStatus = {
    status: Status.Error,
    text: 'Please provide an answer',
  }
  const successStatus = {
    status: Status.Success,
  }

  const isEmptyArray = Array.isArray(answer) && answer.length === 0

  const isMultiSelect = question.type === QuestionType.MultiSelect

  const isEmptyAnswer =
    answer === undefined ||
    answer === UnansweredValue ||
    (typeof answer === 'string' && answer.trim() === '') ||
    (!isMultiSelect && isEmptyArray) ||
    (isMultiSelect && answer === undefined)

  if (question.required && isEmptyAnswer) return errorStatus
  if (!question.required && isEmptyAnswer) return successStatus
  if (question.type === QuestionType.Address) return validateAddress(answer as Address)
  if (!isMultiSelect && isEmptyArray) return errorStatus

  if (
    isMultiSelect &&
    Array.isArray(answer) &&
    !isEmptyArray &&
    !answer.some((a) => question.options?.some((o) => o.key === a))
  ) {
    return { status: Status.Error, text: 'Invalid option' }
  }
  if (question.type === QuestionType.Select && !question.options?.some((o) => o.key === answer)) {
    return { status: Status.Error, text: 'Invalid option' }
  }

  if (!question.validation || question.validation.length === 0) return successStatus

  const rules = question.validation.map((rule) =>
    validationRulesMap[rule.type](rule, question.ui?.validation?.[rule.type], answers),
  )

  if (rules.length === 0) {
    return successStatus
  }
  const validationResult = new Validator(rules).validate(answer)
  if (!validationResult.valid) return { ...errorStatus, text: validationResult.errors[0] }
  return successStatus
}

export function validateAddress(answer: Address): Validation {
  let valid = true
  const fields: string[] = []
  if (answer.street.length === 0) fields.push('street')
  if (answer.city.length === 0) fields.push('city')
  if (answer.postalCode.length === 0 || answer.postalCode.length < 5) fields.push('postalCode')
  if (answer.country.length === 0) fields.push('country')
  if (fields.length > 0) valid = false

  return {
    status: valid ? Status.Success : Status.Error,
    ...(!valid && { text: 'Address information is incomplete or invalid' }),
    ...(fields.length > 0 && { fields: fields }),
  }
}
